import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import type { Insight } from '../insightsData';

interface FeaturedInsightProps {
  insight: Insight;
}

const FeaturedInsight = ({ insight }: FeaturedInsightProps) => {
  return (
    <article className="group">
      <Link
        href={`/insights/${insight.slug}`}
        className="block transition-transform duration-200 ease-confident active:scale-[0.99]"
      >
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="relative overflow-hidden rounded-md aspect-4/3">
            <AppImage
              src={insight.image}
              alt={insight.alt}
              className="w-full h-full object-cover transition-transform duration-500 ease-confident group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-linear-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-4 text-sm font-cta text-muted-foreground">
              <span className="text-primary uppercase">{insight.category}</span>
              <span className="flex items-center gap-1">
                <Icon name="ClockIcon" size={16} />
                {insight.readTime} min read
              </span>
            </div>

            <h2 className="font-headline text-2xl md:text-3xl font-bold text-foreground group-hover:text-primary transition-colors duration-300 line-clamp-3">
              {insight.title}
            </h2>

            <p className="font-body text-lg text-muted-foreground leading-relaxed">
              {insight.excerpt}
            </p>

            <div className="flex items-center justify-between pt-4">
              <div className="flex items-center gap-3">
                <div className="flex w-12 h-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-cta text-base font-semibold">
                  {insight.author.initials}
                </div>
                <div>
                  <p className="font-cta text-base font-medium text-foreground">
                    {insight.author.name}
                  </p>
                  <p className="font-body text-sm text-muted-foreground">{insight.author.role}</p>
                </div>
              </div>

              <span className="font-body text-sm text-muted-foreground">{insight.publishDate}</span>
            </div>

            <div className="flex items-center gap-2 text-info font-cta font-semibold transition-colors duration-200">
              Read Article
              <Icon
                name="ArrowRightIcon"
                size={20}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default FeaturedInsight;
